import { useFeatureFlagStore } from '@moniebook-web/store';
import type { Modifier } from './models/modifier';
import type { Tax } from './models/tax';
import type { CartItem } from './types/cart';
import { FEATURE_FLAGS } from './types/feature-flags';

export async function buildTaxMap(
    cartItems: CartItem[],
): Promise<Map<string, Tax[]>> {
    const taxMap = new Map<string, Tax[]>();

    const isNewTaxModelEnabled =
        useFeatureFlagStore.getState().featureFlags?.[
            FEATURE_FLAGS.ENABLE_ITEM_LEVEL_TAXES
        ] ?? false;

    const itemIds = cartItems
        .map((item) => item.itemId)
        .filter((id): id is string => Boolean(id));

    if (itemIds.length === 0) return taxMap;

    const { itemService, taxService } = await import('./services');

    if (!isNewTaxModelEnabled) {
        const taxes = await taxService.getAll();
        return new Map(
            itemIds.map((itemId) => [
                itemId,
                taxes.map((tax) => ({ ...tax, includedInTotal: false })),
            ]),
        );
    }

    const [items, globalTaxes] = await Promise.all([
        itemService.fetchItemByIds(itemIds),
        taxService.getAllGlobalTaxes(),
    ]);

    const activeTaxIds = items
        .filter((item) => item.taxes)
        .flatMap((item) =>
            (item.taxes ?? [])
                .filter((tax) => !tax.deleted)
                .map((tax) => tax.taxId),
        );

    const itemSpecificTaxes = await taxService.getTaxesByIds(activeTaxIds);
    const taxLookupMap = new Map(itemSpecificTaxes.map((tax) => [tax.id, tax]));
    const taxableItems = items.filter((item) => item.taxable);

    for (const item of taxableItems) {
        const activeTaxReferences =
            item.taxes?.filter((tax) => !tax.deleted) ?? [];

        const itemTaxes = activeTaxReferences
            .map((taxRef) => taxLookupMap.get(taxRef.taxId))
            .filter((tax): tax is Tax => tax !== undefined);

        taxMap.set(item.id, [...(globalTaxes ?? []), ...itemTaxes]);
    }

    return taxMap;
}

export function getModifierLinkedConsumption(
    item: CartItem,
    modifiersById: Map<string, Modifier>,
): Array<{ variantId: string; amount: number }> {
    const selectedOptions = (item.modifiers ?? []).flatMap((selectedGroup) => {
        const modifier = modifiersById.get(selectedGroup.id);

        if (!modifier) return [];

        return selectedGroup.options.map((selectedOption) => ({
            modifier,
            selectedOption,
        }));
    });

    return selectedOptions.flatMap(({ modifier, selectedOption }) => {
        const option = modifier.options.find(
            (candidate) => candidate.id === selectedOption.optionId,
        );

        if (!option?.linkedVariantId) return [];

        return [
            {
                variantId: option.linkedVariantId,
                amount:
                    item.quantity *
                    selectedOption.quantity *
                    option.deductionQuantity,
            },
        ];
    });
}

/**
 * Builds a flat consumption map from cart items.
 * Bundles are decomposed into their child items.
 */
export async function buildConsumptionMap(
    cartItems: CartItem[],
): Promise<Map<string, number>> {
    const map = new Map<string, number>();
    const variantIds = cartItems
        .map((item) => item.variantId)
        .filter((id): id is string => Boolean(id));
    const modifierIds = [
        ...new Set(
            cartItems.flatMap(
                (item) => item.modifiers?.map((modifier) => modifier.id) ?? [],
            ),
        ),
    ];

    if (variantIds.length === 0 && modifierIds.length === 0) {
        return map;
    }

    const { itemService, modifierService } = await import('./services');

    const [{ compositionsByParent }, modifiers] = await Promise.all([
        variantIds.length
            ? itemService.getCompositionVariantMap(variantIds)
            : Promise.resolve({ compositionsByParent: new Map() }),
        modifierIds.length
            ? modifierService.getByIds(modifierIds)
            : Promise.resolve([]),
    ]);

    const modifiersById = new Map(
        modifiers.map((modifier) => [modifier.id, modifier]),
    );

    const addConsumption = (variantId: string, amount: number) => {
        map.set(variantId, (map.get(variantId) ?? 0) + amount);
    };

    for (const item of cartItems) {
        const children = compositionsByParent.get(item.variantId);

        if (children?.length) {
            for (const child of children) {
                addConsumption(child.variantId, item.quantity * child.quantity);
            }
        } else if (item.variantId) {
            addConsumption(
                item.variantId,
                item.quantity * (item.conversionRatio ?? 1),
            );
        }

        for (const { variantId, amount } of getModifierLinkedConsumption(
            item,
            modifiersById,
        )) {
            addConsumption(variantId, amount);
        }
    }

    return map;
}
